import { defineMessages, useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import {
  usePanelContext,
  useShouldPanelBeVisible,
} from '~/eds/src/components/Panel/PanelContext';
import { Text } from '~/eds/src/components/Text/Text';
import type { IconColor } from '~/eds/src/types/IconColor';
import type { IconType } from '~/eds/src/types/IconType';
import type { Task as TaskType } from '~/redux/api/methods/k2/listTasks';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import {
  getTaskDetailsPanelId,
  TaskDetailsPanel,
} from './task-details/TaskDetailsPanel';

const messages = defineMessages({
  viewTaskDetailsLabel: {
    id: 'Task.viewTaskDetailsLabel',
    defaultMessage: 'View details for {name}, Status: {status}',
    description:
      'Accessible label for the task button that opens up the task details panel',
  },
  statusInitialized: {
    id: 'Task.statusInitialized',
    defaultMessage: 'Initialized',
    description: 'Task status',
  },
  statusPending: {
    id: 'Task.statusPending',
    defaultMessage: 'Pending',
    description: 'Task status',
  },
  statusActive: {
    id: 'Task.statusActive',
    defaultMessage: 'Active',
    description: 'Task status',
  },
  statusCompleted: {
    id: 'Task.statusCompleted',
    defaultMessage: 'Completed',
    description: 'Task status',
  },
  statusApproved: {
    id: 'Task.statusApproved',
    defaultMessage: 'Approved',
    description: 'Task status',
  },
  statusRejected: {
    id: 'Task.statusRejected',
    defaultMessage: 'Rejected',
    description: 'Task status',
  },
});

const STATUS_ICON_MAP: Record<TaskType['status'], IconType> = {
  initialized: 'status-not-started',
  pending: 'status-pending',
  active: 'status-pending',
  completed: 'status-success',
  approved: 'status-success',
  rejected: 'status-reject',
};

const getStatusIconColor = (status: TaskType['status']): IconColor => {
  if (status === 'completed' || status === 'approved') {
    return 'status.success';
  }
  if (status === 'rejected') {
    return 'status.danger';
  }
  return 'status.inactive';
};

const getStatusMessageKey = (status: TaskType['status']) => {
  switch (status) {
    case 'initialized':
      return messages.statusInitialized;
    case 'pending':
      return messages.statusPending;
    case 'active':
      return messages.statusActive;
    case 'completed':
      return messages.statusCompleted;
    case 'approved':
      return messages.statusApproved;
    case 'rejected':
      return messages.statusRejected;
    default:
      return messages.statusPending;
  }
};

type Props = {
  task: TaskType;
};

export const Task = ({ task }: Props) => {
  const intl = useIntl();
  const { navigate, params } = useRouting();
  const { openPanel, closePanel } = usePanelContext();
  const panelId = getTaskDetailsPanelId(task.id);
  const isPanelOpen = useShouldPanelBeVisible(panelId);

  const statusLabel = intl.formatMessage(getStatusMessageKey(task.status));
  const accessibleLabel = intl.formatMessage(messages.viewTaskDetailsLabel, {
    name: task.name,
    status: statusLabel,
  });

  const ticketId = params.ticketId!;
  const basePath = RoutePathType.WorkflowCLMEvolutionTicketsDetail.replace(
    ':ticketId',
    ticketId,
  );

  const togglePanel = () => {
    if (isPanelOpen) {
      closePanel(panelId);
      navigate(`${basePath}/tasks`, { replace: true });
    } else {
      openPanel(panelId);
      navigate(`${basePath}/tasks/${task.id}`, { replace: true });
    }
  };

  return (
    <>
      <Box
        display="block"
        aria-label={accessibleLabel}
        as="button"
        borderRadius="m"
        px={4}
        py={2}
        type="button"
        onClick={togglePanel}
        styles={{
          backgroundColor: isPanelOpen ? 'status.active.light' : 'background',
          ':hover': { bg: 'background.quiet' },
        }}
        mb={2}
        w="100%"
      >
        <Layout align="center" justify="start" spacing={2} w="100%">
          <Icon
            color={getStatusIconColor(task.status)}
            icon={STATUS_ICON_MAP[task.status]}
            size="s"
          />
          <Text variant="body-medium">{task.name}</Text>
        </Layout>
      </Box>
      <TaskDetailsPanel panelId={panelId} taskId={task.id} />
    </>
  );
};
